/**
 * Export Templates Settings Section
 * Allows users to configure custom export templates for code generation
 */

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/InputDefault';
import { TextAreaElement } from '@/components/wizards/wizard-elements/TextAreaElement';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/DefaultSelectDropdown';
import { MultiSelectOption } from '@/components/ui/dropdowns/MultiSelectDropdownWithField';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';
import IconProvider from '@/components/ui/IconProvider';
import { getCachedTemplates } from '@/lib/stores/templatesStore';
import { getAvailablePlatformsForGame } from '@/lib/utils/platformMapping';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus, vs } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { useTheme } from '@/providers/ThemeProvider';
import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
import { useGlossaryData } from '@/lib/useObjectsData';
import { useGlossaryBlueprintsDataContext } from '@/providers/GlossaryBlueprintsDataProvider';
import { useTypesDataContext } from '@/providers/TypesDataProvider';
import { useTagsDataContext } from '@/providers/TagsDataProvider';

// Import Prism theme
import '@/styles/prism-theme.css';

// Import extracted components
import { TemplateSidebar } from './TemplateSidebar';
import { TemplateEditor } from './TemplateEditor';
import { TemplatePreviewPanel } from './TemplatePreviewPanel';
import { FieldNameOverridesPreview } from './FieldNameOverridesPreview';
import { FieldExportMappingsPreview } from './FieldExportMappingsPreview';

// Import utilities
import { getPlatformLanguage, highlightCode } from './utils/syntaxHighlighting';
import { getExampleOutputForTemplate } from './utils/templateExamples';

// Import hooks
import { useTemplateManagement } from './hooks/useTemplateManagement';
import { useTemplatePreview } from './hooks/useTemplatePreview';

export default function ExportTemplatesSection() {
  const { t } = usePlatformTranslation();
  const { selectedGame } = useSimplifiedUserData();
  const { effectiveTheme } = useTheme();
  
  const [activeTab, setActiveTab] = useState<'template' | 'object' | 'functions' | 'mappings' | 'preview'>('template');
  
  // Get data for preview generation
  const { items: allItems } = useGlossaryData({
    gameId: selectedGame?.id,
    type: 'library',
    autoLoad: true
  });
  const { glossaryBlueprints } = useGlossaryBlueprintsDataContext();
  const { types } = useTypesDataContext();
  const { tags } = useTagsDataContext();
  
  // Get available templates/blueprints for the current game
  const availableTemplates = selectedGame ? getCachedTemplates(selectedGame.id) : [];
  
  // Convert templates to MultiSelectOptions
  const templateOptions: MultiSelectOption[] = availableTemplates.map(template => ({
    id: template.id,
    label: template.name,
    description: template.id
  }));
  
  // Get syntax theme based on current theme
  const syntaxTheme = effectiveTheme === 'dark' ? vscDarkPlus : vs;
  
  // Use template management hook
  const {
    templates,
    setTemplates,
    activeTemplateId,
    selectedTemplateId,
    setSelectedTemplateId,
    editingTemplate,
    setEditingTemplate,
    isEditing,
    setIsEditing,
    isSaving,
    saveTemplates,
    handleSetActive,
    handleCreateCustom,
    handleDuplicate,
    handleEdit,
    handleSaveEdit,
    handleCancelEdit,
    handleDelete
  } = useTemplateManagement({ selectedGame });
  
  // Use template preview hook
  const {
    previewContent,
    translationKeysPreview,
    typesPreview,
    tagsPreview,
    dialogDatabasePreview,
    dialogVariablesPreview,
    previewFileType,
    setPreviewFileType,
    isGeneratingPreview,
    previewNamingConvention,
    setPreviewNamingConvention,
    generatePreview
  } = useTemplatePreview({
    selectedGame,
    allItems: allItems || [],
    glossaryBlueprints: glossaryBlueprints || [],
    types: types || [],
    tags: tags || []
  });
  
  // Get available platforms for the game
  const availablePlatforms = getAvailablePlatformsForGame(selectedGame);
  
  // Filter templates to only show those matching the game's platforms
  const filteredTemplates = templates.filter(template => 
    availablePlatforms.includes(template.platform)
  );
  
  // Get current selected template
  const selectedTemplate = filteredTemplates.find(t => t.id === selectedTemplateId);
  const activeTemplate = filteredTemplates.find(t => t.id === activeTemplateId);
  
  // Auto-switch to 'object' tab when selecting a database template (which has typeFile disabled)
  useEffect(() => {
    if (selectedTemplate && !selectedTemplate.typeFile.enabled && (activeTab === 'template' || activeTab === 'functions')) {
      setActiveTab('object');
    }
  }, [selectedTemplateId, selectedTemplate, activeTab]);
  
  if (!selectedGame) {
    return (
      <div className="p-4 border border-warning bg-warning/10 rounded">
        <p className="text-warning-foreground text-center">
          Please select a game to configure export templates
        </p>
      </div>
    );
  }
  
  return (
    <div className="max-w-6xl space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="heading-3">{t('platform.settings.exporttemplatessection.export_templates')}</h1>
          <p className="text-muted-foreground mt-2">
            Configure how templates and library objects are exported to code.
            Customize file structure, naming conventions, and type mappings.
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            <strong>Note:</strong> Only the active template will be saved for this game.
          </p>
          {selectedGame?.settings?.engine && (
            <p className="text-sm text-muted-foreground mt-1">
              <strong>{t('platform.settings.exporttemplatessection.game_engine')}</strong> {selectedGame.settings.engine}
              {' • '}
              <strong>{t('platform.settings.exporttemplatessection.showing_templates_for')}</strong> {availablePlatforms.join(', ')}
            </p>
          )}
        </div>
        
        <Button
          onClick={saveTemplates}
          disabled={isSaving}
          size="lg"
          className="gap-2"
        >
          {isSaving ? (
            <>
              <IconProvider iconstring="fa-spinner" className="animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <IconProvider iconstring="fa-save" />
              Save Active Template
            </>
          )}
        </Button>
      </div>
      
      <div className="grid grid-cols-12 gap-6">
        {/* Template List Sidebar */}
        <TemplateSidebar
          templates={filteredTemplates}
          selectedTemplateId={selectedTemplateId}
          activeTemplateId={activeTemplateId}
          onSelectTemplate={setSelectedTemplateId}
          onCreateCustom={handleCreateCustom}
          canCreateCustom={!!selectedTemplate}
        />
        
        {/* Template Details */}
        <div className="col-span-9">
          {selectedTemplate && !isEditing ? (
            <div className="border border-border rounded p-6 bg-card space-y-6">
              {/* Template Header */}
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="heading-4">{selectedTemplate.name}</h2>
                  <div className="text-sm text-muted-foreground mt-1">
                    <TextAreaElement
                      value={selectedTemplate.description || 'No description'}
                      disabled={true}
                      viewMode="view-formatted"
                      className="border-0 p-0 bg-transparent"
                    />
                  </div>
                  <div className="flex gap-2 mt-2">
                    <span className="text-xs px-2 py-1 bg-primary/10 text-primary rounded">
                      {selectedTemplate.platform.toUpperCase()}
                    </span>
                    {selectedTemplate.isPreset && (
                      <span className="text-xs px-2 py-1 bg-accent text-accent-foreground rounded">
                        Preset
                      </span>
                    )}
                    {activeTemplateId === selectedTemplate.id && (
                      <span className="text-xs px-2 py-1 bg-primary text-primary-foreground rounded">
                        Active
                      </span>
                    )}
                  </div>
                </div>
                
                <div className="flex gap-2">
                  {activeTemplateId !== selectedTemplate.id && (
                    <Button
                      onClick={() => handleSetActive(selectedTemplate.id)}
                      variant="outline"
                      size="sm"
                      className="gap-2"
                    >
                      <IconProvider iconstring="fa-check" size={12} />
                      Set Active
                    </Button>
                  )}
                  
                  <Button
                    onClick={() => handleDuplicate(selectedTemplate)}
                    variant="outline"
                    size="sm"
                    className="gap-2"
                  >
                    <IconProvider iconstring="fa-copy" size={12} />
                    Duplicate
                  </Button>
                  
                  <Button
                    onClick={() => handleEdit(selectedTemplate)}
                    variant="outline"
                    size="sm"
                    className="gap-2"
                  >
                    <IconProvider iconstring="fa-edit" size={12} />
                    Edit
                  </Button>
                  
                  {!selectedTemplate.isPreset && (
                    <Button
                      onClick={() => handleDelete(selectedTemplate.id)}
                      variant="outline"
                      size="sm"
                      className="gap-2 text-destructive hover:text-destructive"
                    >
                      <IconProvider iconstring="fa-trash" size={12} />
                      Delete
                    </Button>
                  )}
                </div>
              </div>
              
              {/* Tabs */}
              <div className="w-full">
                <div className="flex gap-1 border-b border-border mb-4">
                  {selectedTemplate.typeFile.enabled && (
                    <button
                      onClick={() => setActiveTab('template')}
                      className={`px-4 py-2 text-sm font-medium transition-colors ${
                        activeTab === 'template'
                          ? 'border-b-2 border-primary text-primary'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      Template Structure
                    </button>
                  )}
                  <button
                    onClick={() => setActiveTab('object')}
                    className={`px-4 py-2 text-sm font-medium transition-colors ${
                      activeTab === 'object'
                        ? 'border-b-2 border-primary text-primary'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    Output Structure
                  </button>
                  {selectedTemplate.typeFile.enabled && (
                    <button
                      onClick={() => setActiveTab('functions')}
                      className={`px-4 py-2 text-sm font-medium transition-colors ${
                        activeTab === 'functions'
                          ? 'border-b-2 border-primary text-primary'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      Functions
                    </button>
                  )}
                  <button
                    onClick={() => setActiveTab('mappings')}
                    className={`px-4 py-2 text-sm font-medium transition-colors ${
                      activeTab === 'mappings'
                        ? 'border-b-2 border-primary text-primary'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    Naming & Mappings
                  </button>
                  <button
                    onClick={() => setActiveTab('preview')}
                    className={`px-4 py-2 text-sm font-medium transition-colors ${
                      activeTab === 'preview'
                        ? 'border-b-2 border-primary text-primary'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    Preview
                  </button>
                </div>
                
                {/* Template Tab */}
                {activeTab === 'template' && selectedTemplate.typeFile.enabled && (
                  <div className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label>Filename Pattern</Label>
                      <div className="p-2 bg-accent/50 rounded font-mono text-sm">
                        {selectedTemplate.typeFile.filename}
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Location in Package</Label>
                      <div className="p-2 bg-accent/50 rounded font-mono text-sm">
                        {selectedTemplate.typeFile.location}/
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Template Content</Label>
                      <div className="rounded-lg border border-border overflow-hidden">
                        <SyntaxHighlighter
                          language={getPlatformLanguage(selectedTemplate.platform)}
                          style={syntaxTheme}
                          customStyle={{
                            margin: 0,
                            fontSize: '0.75rem',
                            maxHeight: '400px',
                          }}
                          showLineNumbers={true}
                        >
                          {selectedTemplate.typeFile.content}
                        </SyntaxHighlighter>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Object Tab */}
                {activeTab === 'object' && (
                  <div className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label>Filename Pattern</Label>
                      <div className="p-2 bg-accent/50 rounded font-mono text-sm">
                        {selectedTemplate.instanceFile.filename || '{ClassName}.cs'}
                      </div>
                    </div>
                    
                    {selectedTemplate.instanceFile.location && (
                      <div className="space-y-2">
                        <Label>Location in Package</Label>
                        <div className="p-2 bg-accent/50 rounded font-mono text-sm">
                          {selectedTemplate.instanceFile.location}/
                        </div>
                      </div>
                    )}
                    
                    <div className="space-y-2">
                      <Label>{selectedTemplate.typeFile.enabled ? 'Template Content' : 'Output Structure Example'}</Label>
                      {!selectedTemplate.typeFile.enabled && (
                        <p className="text-xs text-muted-foreground mb-2">
                          This template generates code programmatically. Below is an example of the output structure.
                        </p>
                      )}
                      <div className="rounded-lg border border-border overflow-hidden">
                        <SyntaxHighlighter
                          language={getPlatformLanguage(selectedTemplate.platform)}
                          style={syntaxTheme}
                          customStyle={{
                            margin: 0,
                            fontSize: '0.75rem',
                            maxHeight: '500px',
                          }}
                          showLineNumbers={true}
                        >
                          {selectedTemplate.instanceFile.content || getExampleOutputForTemplate(selectedTemplate.id)}
                        </SyntaxHighlighter>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Functions Tab */}
                {activeTab === 'functions' && selectedTemplate.typeFile.enabled && (
                  <div className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label>Template Functions</Label>
                      <p className="text-sm text-muted-foreground">
                        Functions that will be added to each object instance. These functions can access object fields.
                      </p>
                      {selectedTemplate.functions && selectedTemplate.functions.length > 0 ? (
                        <div className="space-y-3">
                          {selectedTemplate.functions.map((func, index) => (
                            <div key={index} className="p-4 border border-border rounded bg-accent/5">
                              <div className="font-medium mb-1">{func.name}</div>
                              {func.description && (
                                <div className="text-sm text-muted-foreground mb-2">{func.description}</div>
                              )}
                              <div className="text-xs text-muted-foreground">
                                Applies to: {func.appliesTo === 'all' ? 'All templates' : `${Array.isArray(func.appliesTo) ? func.appliesTo.length : 0} template(s)`}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="p-4 border border-dashed rounded text-center text-muted-foreground text-sm">
                          No functions defined
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Mappings Tab */}
                {activeTab === 'mappings' && (
                  <div className="space-y-4 mt-4">
                    <div className="space-y-4">
                      <Label className="text-base font-semibold">Naming Conventions</Label>
                      <div className="grid grid-cols-2 gap-4 p-4 border border-border rounded bg-accent/5">
                        <div className="space-y-1">
                          <div className="text-xs text-muted-foreground">{t('platform.settings.exporttemplatessection.template_name_convention')}</div>
                          <div className="p-2 bg-accent/50 rounded text-sm">
                            {selectedTemplate.namingConventions.templateName}
                          </div>
                        </div>
                        <div className="space-y-1">
                          <div className="text-xs text-muted-foreground">{t('platform.settings.exporttemplatessection.field_name_convention')}</div>
                          <div className="p-2 bg-accent/50 rounded text-sm">
                            {selectedTemplate.namingConventions.fieldName}
                          </div>
                        </div>
                        <div className="space-y-1">
                          <div className="text-xs text-muted-foreground">{t('platform.settings.exporttemplatessection.instance_name_convention')}</div>
                          <div className="p-2 bg-accent/50 rounded text-sm">
                            {selectedTemplate.namingConventions.instanceName}
                          </div>
                        </div>
                        <div className="space-y-1">
                          <div className="text-xs text-muted-foreground">{t('platform.settings.exporttemplatessection.file_name_convention')}</div>
                          <div className="p-2 bg-accent/50 rounded text-sm">
                            {selectedTemplate.namingConventions.fileName}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <FieldNameOverridesPreview 
                      template={selectedTemplate}
                      availableTemplates={availableTemplates}
                    />
                    
                    <FieldExportMappingsPreview 
                      template={selectedTemplate}
                    />
                  </div>
                )}
                
                {/* Preview Tab */}
                {activeTab === 'preview' && (
                  <TemplatePreviewPanel
                    template={selectedTemplate}
                    previewContent={previewContent}
                    translationKeysPreview={translationKeysPreview}
                    typesPreview={typesPreview}
                    tagsPreview={tagsPreview}
                    dialogDatabasePreview={dialogDatabasePreview}
                    dialogVariablesPreview={dialogVariablesPreview}
                    previewFileType={previewFileType}
                    setPreviewFileType={setPreviewFileType}
                    previewNamingConvention={previewNamingConvention}
                    setPreviewNamingConvention={setPreviewNamingConvention}
                    isGeneratingPreview={isGeneratingPreview}
                    onGeneratePreview={() => generatePreview(selectedTemplate)}
                    effectiveTheme={effectiveTheme}
                  />
                )}
              </div>
            </div>
          ) : isEditing && editingTemplate ? (
            <TemplateEditor
              template={editingTemplate}
              onChange={setEditingTemplate}
              onSave={handleSaveEdit}
              onCancel={handleCancelEdit}
              effectiveTheme={effectiveTheme}
              highlightCode={highlightCode}
              templateOptions={templateOptions}
              availableTemplates={availableTemplates}
            />
          ) : (
            <div className="border border-border rounded p-6 bg-card text-center text-muted-foreground">
              Select a template to view details
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
